import React, { useId } from "react";
import { Calendar, Building2, Tag, ExternalLink, Clock } from "lucide-react";
import { useLang } from "@/context/TranslationProvider";
import { useMyDialog } from "@/context/MyDialogProvider";
import GazetteAnnouncementDetail from "./GazetteAnnouncementDetail";

type Announcement = {
  page: number;
  type: string;
  office: string;
  title: string;
  link: string;
  date: string;
  deadline: string;
  postedAgo: string;
  language?: string; // Optional language field for potential future use
};

interface AnnouncementCardProps {
  item: Announcement;
}

export default function AnnouncementCard({ item }: AnnouncementCardProps) {
  const { t } = useLang();
  const { open, close } = useMyDialog();
  const isRTL = item.language === 'ar' || item.language === 'dv'; // Example check for RTL languages, adjust as needed

  const viewDetails = (uid: any) => {
  open({
    content: (
      <div className="flex justify-center">
        <GazetteAnnouncementDetail uid={uid} deadline={item.deadline} url={item.link} />
      </div>
    ),
  });
};

  return (
    <div
      
      dir={isRTL ? "rtl" : "ltr"}
      className="dhi group h-full flex flex-col bg-white rounded-2xl border border-gray-100 shadow-sm hover:shadow-xl transition-all duration-300 overflow-hidden"
    >
      {/* Gradient accent bar */}
      <div className="h-1.5 bg-gradient-to-r from-blue-500 via-blue-600 to-indigo-600" />

      {/* Content wrapper */}
      <div className="p-2 flex flex-col flex-grow">

        {/* Type badge */}
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-1.5 px-2.5 py-1 bg-blue-50 rounded-full">
            <Tag className="w-3.5 h-3.5 text-blue-600" />
            <span className="text-[10px] font-medium text-blue-700 uppercase tracking-wide">
              {item.type}
            </span>
          </div>

          {/* Posted ago badge */}
          <div className="flex items-center gap-1 text-[10px] text-gray-400">
            <Clock className="w-3 h-3" />
            <span>{item.postedAgo}</span>
          </div>
        </div>

        {/* Title */}
        <div onClick={() => viewDetails(getGazetteId(item.link))} className="">

        <h3 className="bg-amber-400 h-full text-md font-bold text-indigo-800 line-clamp-3 mb-2 group-hover:text-blue-600 transition-colors text-justify bg-indigo-200 -mx-2 p-2">
          {item.title}
        </h3>
        </div>

        {/* Office */}
        <div className="h-fit  flex items-center gap-1.5 text-sm text-gray-600 mb-2">
          <Building2 className="w-4 h-4 text-gray-400 flex-shrink-0" />
          <span className="line-clamp-1 text-xs">{item.office}</span>
        </div>

        {/* Spacer to push dates to bottom */}
        <div className="flex-grow bg-amber-100" />
        {/* Dates section */}
        <div className="space-y-2 pt-4 border-t border-gray-100 mt-3">
          <div className="flex items-center justify-between text-sm">
            <div className="flex items-center gap-1.5 text-gray-500">
              <Calendar className="w-3.5 h-3.5" />
              <span className="text-xs">{t("announcement.published")}</span>
            </div>
            <span className="text-xs font-medium text-gray-700">{item.date}</span>
          </div>

          <div className="flex items-center justify-between text-sm">
            <div className="flex items-center gap-1.5 text-gray-500">
              <Calendar className="w-3.5 h-3.5" />
              <span className="text-xs">{t("announcement.deadline")}</span>
            </div>
            <span className="text-xs font-semibold text-red-600">{item.deadline}</span>
          </div>
        </div>

        {/* Action button */}
        <a
          href={item.link}
          target="_blank"
          rel="noopener noreferrer"
          className="mt-5 inline-flex items-center justify-between gap-2 px-4 py-2.5 bg-gray-50 hover:bg-blue-50 rounded-xl text-sm font-medium text-gray-700 hover:text-blue-700 transition-all duration-200 group/link"
        >
          <span>{t("announcement.readMore")}</span>
          <ExternalLink className="w-4 h-4 group-hover/link:translate-x-0.5 group-hover/link:-translate-y-0.5 transition-transform" />
        </a>

      </div>
    </div>
  );
}

const getGazetteId = (url:string) => {
  const match = url.match(/\/iulaan\/(\d+)/);
  return match ? match[1] : null;
};
